// Asking the suggestions for the text a person types in the Search tab: the
// terminal's rule (terminal/desktop/src/shell/searcher.ts, `createAsker` with
// `oneAtATime`; its CLAUDE.md, "Suggestions while typing"):
//   - asked DELAY_MS after the typing stops, from MIN_CHARS characters;
//   - never twice for the same text;
//   - at most one request out at a time: a text whose pause ends while a
//     request is out waits for it to land, and only the latest text is asked;
//   - a busy answer (429) for the text on screen is asked ONCE more after
//     BUSY_RETRY_MS (the backend's Retry-After), and the state says "busy"
//     meanwhile; a second refusal is an error;
//   - an answer, busy or not, for a text that changed is dropped in silence
//     (the call itself cannot be taken back);
//   - after a failure, `retry()` asks the same text again (Enter, or a Retry
//     button), with the same rules (no pause: the person asked for it).
//
// Pure (timers injected): no Office.js, no DOM.

export const DELAY_MS = 250;
export const BUSY_RETRY_MS = 5000;
/** The hub wants at least two characters. */
export const MIN_CHARS = 2;

export interface AskState<A> {
  /** The text the state is about; null when nothing is asked. */
  query: string | null;
  /**
   * `waiting`: the pause before asking. `loading`: asked (or waiting for its
   * turn), no answer yet. `busy`: refused as busy, asked once more after
   * BUSY_RETRY_MS. `error`: the call failed (`error` holds why).
   */
  status: "idle" | "waiting" | "loading" | "busy" | "done" | "error";
  answer: A | null;
  error: unknown;
}

export const NO_ASK: AskState<never> = { query: null, status: "idle", answer: null, error: null };

export interface Timers {
  set(run: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

const realTimers: Timers = {
  set: (run, ms) => setTimeout(run, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface AskerOptions<A> {
  delayMs?: number;
  busyRetryMs?: number;
  onChange(state: AskState<A>): void;
  timers?: Timers;
  /** Whether a failure is "busy" (asked once more). Default: an error with status 429. */
  isBusy?(e: unknown): boolean;
}

export interface Asker<A> {
  /** Ask this text (null, or under MIN_CHARS once trimmed: nothing). Calling it again replaces what was asked. */
  query(text: string | null): void;
  /** Ask the text on screen again after a failure (status `error`); otherwise nothing. */
  retry(): void;
  /** The last state handed to onChange. */
  state(): AskState<A>;
  dispose(): void;
}

const defaultIsBusy = (e: unknown): boolean => (e as { status?: unknown } | null)?.status === 429;

export function createAsker<A>(fetch: (q: string) => Promise<A>, options: AskerOptions<A>): Asker<A> {
  const delayMs = options.delayMs ?? DELAY_MS;
  const busyRetryMs = options.busyRetryMs ?? BUSY_RETRY_MS;
  const timers = options.timers ?? realTimers;
  const isBusy = options.isBusy ?? defaultIsBusy;

  let current: string | null = null;
  let last: AskState<A> = NO_ASK;
  let timer: unknown = null;
  /** Grows with every text: an answer for an older text is dropped. */
  let turn = 0;
  let flying = false;
  /** What to send when the request out lands. */
  let queued: (() => void) | null = null;

  const emit = (s: AskState<A>) => {
    last = s;
    options.onChange(s);
  };
  const stop = () => {
    if (timer !== null) timers.clear(timer);
    timer = null;
    queued = null;
  };
  const landed = () => {
    flying = false;
    const next = queued;
    queued = null;
    next?.();
  };

  /** Ask `wanted` as a new turn, after `pause` ms. */
  const start = (wanted: string, pause: number) => {
    turn += 1;
    stop();
    const mine = turn;
    emit({ query: wanted, status: "waiting", answer: null, error: null });

    const send = (again: boolean) => {
      flying = true;
      fetch(wanted).then(
        (answer) => {
          if (mine === turn) emit({ query: wanted, status: "done", answer, error: null });
          landed();
        },
        (reason: unknown) => {
          if (mine === turn && again && isBusy(reason)) {
            emit({ query: wanted, status: "busy", answer: null, error: null });
            timer = timers.set(() => {
              timer = null;
              ask(false);
            }, busyRetryMs);
          } else if (mine === turn) {
            emit({ query: wanted, status: "error", answer: null, error: reason });
          }
          landed();
        }
      );
    };
    const ask = (again: boolean) => {
      if (flying) queued = () => send(again);
      else send(again);
    };
    timer = timers.set(() => {
      timer = null;
      emit({ query: wanted, status: "loading", answer: null, error: null });
      ask(true);
    }, pause);
  };

  return {
    query(text) {
      const wanted = text === null || text.trim().length < MIN_CHARS ? null : text.trim();
      if (wanted === current) return;
      current = wanted;
      if (wanted === null) {
        turn += 1;
        stop();
        emit(NO_ASK);
        return;
      }
      start(wanted, delayMs);
    },
    retry() {
      if (current === null || last.status !== "error" || last.query !== current) return;
      start(current, 0);
    },
    state: () => last,
    dispose() {
      turn += 1;
      current = null;
      stop();
    },
  };
}
